import { Capabilities, PlatformType } from "@spawn/common";
import {
	Align,
	I18NextProvider,
	IAssetHandle,
	IOverlayOptions,
	ITextOptions,
	Layout,
	Orientation,
	Overlay,
	ScreenOrientationService,
	Sprite,
	Stretch,
	Text,
	TextureResource,
	View,
	ViewFixedSizeScalable,
	asset,
	destroy,
	inject,
	subscription,
	view,
} from "@spawn/core";

import { Subscription } from "rxjs";
import Victor from "victor";
import { NrgBackgroundView, FGViewUtils, TaggedText, TextStyles } from "@spawn/nrg-games-core";

@view
export class FreegameIntroView extends Overlay {
	@inject private readonly screenOrientationService!: ScreenOrientationService;
	@inject private readonly i18n!: I18NextProvider;
	@inject private readonly fgViewUtils!: FGViewUtils;

	@subscription subscriptions: Subscription[] = [];

	@asset(TextureResource) public readonly fgReelsTexture!: IAssetHandle<TextureResource>;
	@asset(TextureResource) public readonly fgMysteryTexture!: IAssetHandle<TextureResource>;

	protected containerView!: View;
	protected freegamesText!: Text;
	protected background!: NrgBackgroundView;
	protected fgReelsView!: View;
	protected infoView!: View;
	protected taggedText!: TaggedText;

	constructor(options: IOverlayOptions) {
		super({ ...options, layout: new Layout(Stretch.Horizontal, Stretch.Vertical) });

		this.addBackground();
		this.addContainerView();
		this.addFgText();
		this.addFgReelsSprite();
		this.addInfoText();

		this.subscriptions.push(this.screenOrientationService.orientationChanged$.subscribe(() => this.onOrientationChange()));
	}

	@destroy
	public onDestroy(): void {
		this.fgViewUtils.setGameConsoleVisibility(true);
	}

	public addBackground(): void {
		this.background = new NrgBackgroundView({ dimValue: 0.5, textureIndex: 1 });
		this.addChild(this.background);
	}

	public addContainerView(): void {
		this.containerView = new View({
			layout: new Layout(Align.CenterHorizontal, Align.CenterVertical),
		});
		this.addChild(this.containerView);
	}

	public addFgText(): void {
		this.freegamesText = new Text({
			...TextStyles.Gold,
			fontSize: Capabilities.platformType === PlatformType.Desktop ? 80 : 100,
			text: this.i18n.t("general.freegames"),
			...(this.fgViewUtils.isPortraitMobile && { wordWrapWidth: 900 }),
		});
		this.freegamesText.layout = new Layout(Align.CenterHorizontal, Align.CenterVertical);
		this.freegamesText.position = new Victor(0, -400);

		this.containerView.addChild(this.freegamesText);
	}

	public addFgReelsSprite(): void {
		const fgReelsSprite = new Sprite(this.fgReelsTexture(), new Layout(Align.CenterHorizontal, Align.CenterVertical));
		this.fgReelsView = new ViewFixedSizeScalable({
			layout: new Layout(Align.CenterHorizontal, Align.CenterVertical),
		});
		fgReelsSprite.scale = Capabilities.platformType === PlatformType.Desktop ? new Victor(1.3, 1.3) : new Victor(1.5, 1.5);
		fgReelsSprite.position = new Victor(10, -100);

		const perSpinText = this.i18n.t("general.perSpin");
		const baseFontSize = Capabilities.platformType === PlatformType.Desktop ? 40 : 45;
		// Adjust font size based on text length
		const adjustedFontSize = Math.max(baseFontSize * (8 / perSpinText.length), baseFontSize * 0.6);

		const reelText = TextStyles.default(perSpinText, {
			layout: new Layout(Align.CenterHorizontal, Align.CenterVertical),
			fontSize: adjustedFontSize,
		});
		reelText.position = Capabilities.platformType === PlatformType.Desktop ? new Victor(-50, -217) : new Victor(-57, -235);

		this.fgReelsView.addChild(fgReelsSprite, reelText);
		this.containerView.addChild(this.fgReelsView);
	}

	public addInfoText(): void {
		this.infoView = new ViewFixedSizeScalable({
			layout: new Layout(Align.CenterHorizontal, Align.CenterVertical),
		});

		const fontSize = Capabilities.platformType === PlatformType.Desktop ? 33 : 40;
		const unlimitedText = new Text({
			...TextStyles.Gold,
			fontSize: Capabilities.platformType === PlatformType.Desktop ? 44 : 55,
			text: this.i18n.t("nudgenrefeveal.fg.unlimited"),
		});
		unlimitedText.layout = new Layout(Align.CenterHorizontal, Align.CenterVertical);
		unlimitedText.position = new Victor(0, 120);

		const textStyle: ITextOptions = {
			...TextStyles.White,
			fontSize,
			text: "DUMMY TEXT",
			wordWrap: true,
			wordWrapWidth: Capabilities.platformType === PlatformType.Desktop ? 710 : 880,
			align: "center",
		};

		// This is needed to calculate the correct scale for the mystery image (it is somehow off in Firefox).
		const dummyText = new TaggedText(textStyle);
		const imgScale = 3 * (fontSize / dummyText.realFontSize);

		let mysteryText = this.i18n.t("nudgenreveal.fg1", { symbolId: `<mystery imgDisplay="icon" iconScale="${imgScale}" />` });
		mysteryText = mysteryText.replace(/\n/g, " ");

		const mysteryTexture = new Sprite(this.fgMysteryTexture()).texture;

		this.taggedText = new TaggedText(
			{
				...textStyle,
				text: mysteryText,
			},
			{
				imgMap: {
					mystery: mysteryTexture,
				},
			}
		);
		this.infoView.addChild(unlimitedText, this.taggedText);
		this.containerView.addChild(this.infoView);
	}

	public onOrientationChange(): void {
		this.fgViewUtils.setGameConsoleVisibility(true);

		if (Capabilities.platformType === PlatformType.Desktop) {
			this.taggedText.position = new Victor(-355, 150);
		} else if (this.screenOrientationService.orientation === Orientation.Portrait) {
			this.fgViewUtils.setGameConsoleVisibility(false);
			this.containerView.position = new Victor(0, -150);
			this.taggedText.position = new Victor(-440, 150);
			this.infoView.position = new Victor(0, 30);
		} else {
			this.containerView.position = new Victor(0, 0);
			this.taggedText.position = new Victor(-440, 150);
			this.infoView.position = new Victor(0, 30);
		}
	}
}
